import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaRocket, 
  FaUsers, 
  FaLightbulb, 
  FaGlobe,
  FaHeart,
  FaAward,
  FaGraduationCap,
  FaHandshake,
  FaChartLine,
  FaShieldAlt,
  FaDownload
} from "react-icons/fa";
import Logo from "../components/Logo";
import "../styles/Career.css";

function Career() {
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);

  // Handle scroll for header transparency
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      
      // Get the actual hero section element
      const heroSection = document.querySelector('.career-hero');
      
      if (heroSection) {
        // Get the bottom of the hero section
        const heroBottom = heroSection.offsetHeight - 100; // Subtract a bit for smooth transition
        
        // Add 'scrolled' class when user scrolls past hero section
        setIsHeaderScrolled(scrollPosition > heroBottom);
      } else {
        // Fallback: use viewport height if hero section not found
        setIsHeaderScrolled(scrollPosition > window.innerHeight * 0.9);
      }
    };

    // Add scroll listener
    window.addEventListener('scroll', handleScroll);
    
    // Check initial scroll position after a brief delay to ensure DOM is ready
    const timer = setTimeout(handleScroll, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="career-container">
      <header className={`career-header ${isHeaderScrolled ? 'scrolled' : ''}`}>
        <Logo size="small" showText={false} linkTo="/" />
        <nav className="nav-links">
          <Link to="/downloads" className="download-nav-link"><FaDownload /> Download</Link>
          <Link to="/login" className="login-link">Login</Link>
          <Link to="/register" className="register-link">Register</Link>
        </nav>
      </header>

      <main className="career-main">
        {/* Hero Section */}
        <section className="career-hero">
          <div className="container">
            <div className="career-hero-content">
              <h1>Join the Future of AI-Powered Security</h1>
              <p className="career-subtitle">
                Help us build the next generation of endpoint security research platform 
                that empower security professionals worldwide.
              </p>
              <div className="career-stats">
                <div className="stat">
                  <FaUsers className="stat-icon" />
                  <span className="stat-number">Remote-First</span>
                  <span className="stat-label">Global Team</span>
                </div>
                <div className="stat">
                  <FaRocket className="stat-icon" />
                  <span className="stat-number">AI-Powered</span>
                  <span className="stat-label">Technology</span>
                </div>
                <div className="stat">
                  <FaGlobe className="stat-icon" />
                  <span className="stat-number">Deep research</span>
                  <span className="stat-label">Mission</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="career-values">
          <div className="container">
            <h2>Our Values</h2>
            <div className="values-grid">
              <div className="value-card">
                <div className="value-icon">
                  <FaShieldAlt />
                </div>
                <h3>Security First</h3>
                <p>We believe in building secure, reliable tools that protect organizations and individuals from evolving threats.</p>
              </div>
              <div className="value-card">
                <div className="value-icon">
                  <FaLightbulb />
                </div>
                <h3>Innovation</h3>
                <p>We push the boundaries of what's possible with AI and security research, creating tools that didn't exist before.</p>
              </div>
              <div className="value-card">
                <div className="value-icon">
                  <FaHandshake />
                </div>
                <h3>Collaboration</h3>
                <p>We work together as a team, sharing knowledge and supporting each other to achieve common goals.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="career-benefits">
          <div className="container">
            <h2>Why Join Triagz Team?</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <FaUsers className="benefit-icon" />
                <h3>Remote-First Culture</h3>
                <p>Work from anywhere in the world with flexible hours and a strong remote culture.</p>
              </div>
              <div className="benefit-item">
                <FaGraduationCap className="benefit-icon" />
                <h3>Continuous Learning</h3>
                <p>Access to training, conferences, and learning resources to grow your skills.</p>
              </div>
              <div className="benefit-item">
                <FaHeart className="benefit-icon" />
                <h3>Health & Wellness</h3>
                <p>Comprehensive health coverage and wellness programs for you and your family.</p>
              </div>
              <div className="benefit-item">
                <FaChartLine className="benefit-icon" />
                <h3>Growth Opportunities</h3>
                <p>Clear career progression paths and opportunities to take on new challenges.</p>
              </div>
              <div className="benefit-item">
                <FaAward className="benefit-icon" />
                <h3>Competitive Compensation</h3>
                <p>Fair and competitive salary packages with equity participation.</p>
              </div>
              <div className="benefit-item">
                <FaRocket className="benefit-icon" />
                <h3>Cutting-Edge Tech</h3>
                <p>Work with the latest AI technologies and security research tools.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Current Openings Section */}
        <section className="career-openings">
          <div className="container">
            <h2>Current Opportunities</h2>
            <div className="openings-content">
              <div className="no-openings">
                <div className="no-openings-icon">
                  <FaRocket />
                </div>
                <h3>We're Not Hiring Right Now</h3>
                <p>
                  We currently don't have any open positions, but we're always interested in 
                  connecting with talented individuals who share our passion for security research 
                  and AI-powered solutions.
                </p>
                <p>
                  If you're passionate about cybersecurity, AI, and open-source development, 
                  we'd love to hear from you for future opportunities.
                </p>
                <Link to="/contact" className="btn btn-primary">
                  Get in Touch
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="career-team">
          <div className="container">
            <h2>Join Our Mission</h2>
            <div className="team-content">
              <div className="team-text">
                <h3>Building the Future of Security Research</h3>
                <p>
                  At Triagz, we're on a mission to democratize security research by making 
                  powerful AI-driven tools accessible to security professionals worldwide. 
                  Our platform combines cutting-edge AI with practical security research 
                  capabilities, enabling teams to analyze threats, investigate incidents, 
                  and protect their organizations more effectively.
                </p>
                <p>
                  We're looking for passionate individuals who want to make a real impact 
                  in the cybersecurity field. Whether you're a seasoned security expert, 
                  an AI researcher, or a talented developer, there's a place for you on our team.
                </p>
              </div>
              <div className="team-image">
                <div className="team-mockup">
                  <div className="mockup-header">
                    <span className="mockup-dot"></span>
                    <span className="mockup-dot"></span>
                    <span className="mockup-dot"></span>
                  </div>
                  <div className="mockup-body">
                    <div className="code-line">
                      <span className="code-keyword">const</span>
                      <span className="code-variable"> mission</span>
                      <span className="code-operator"> = </span>
                      <span className="code-string">"democratize security research"</span>
                    </div>
                    <div className="code-line">
                      <span className="code-keyword">const</span>
                      <span className="code-variable"> values</span>
                      <span className="code-operator"> = [</span>
                    </div>
                    <div className="code-line indent">
                      <span className="code-string">"innovation"</span>
                      <span className="code-operator">,</span>
                    </div>
                    <div className="code-line indent">
                      <span className="code-string">"collaboration"</span>
                      <span className="code-operator">,</span>
                    </div>
                    <div className="code-line indent">
                      <span className="code-string">"open source"</span>
                    </div>
                    <div className="code-line">
                      <span className="code-operator">]</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="career-cta">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Make an Impact?</h2>
              <p>
                Even though we don't have open positions right now, we're always excited 
                to connect with talented people who share our vision.
              </p>
              <div className="cta-buttons">
                <Link to="/contact" className="btn btn-primary">
                  Contact Us
                </Link>
                <Link to="/blog" className="btn btn-secondary">
                  Read Our Blog
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default Career; 